import { getDateTimeFormatter } from '@fe/core';
import React from 'react';
import { GDFFcaRecord, GDFFcaType } from '../../gdf/type';
import { getCopyButtonComponent } from '../copy-button';
import { convert } from 'html-to-text';

export default function FcaProperties({ record }: { record: GDFFcaRecord }) {
    const formatter = getDateTimeFormatter();
    const ref = React.useRef<HTMLDivElement | null>(null);
    const Button = getCopyButtonComponent();

    const handleGetText = () => {
        if (!ref.current) {
            return null;
        }

        return convert(ref.current.innerHTML).replaceAll(':\n', ': ');
    };

    return (
        <div>
            {Button && (
                <div className="d-flex justify-content-start">
                    <Button getText={handleGetText} />
                </div>
            )}
            <div ref={ref} className="pt-2">
                <div style={{ display: 'grid', gridTemplateColumns: 'max-content auto', columnGap: '0.5rem' }}>
                    <div>ID:</div>
                    <div>{record.label}</div>

                    <div>Type</div>
                    <div>
                        <span>{record.subType === GDFFcaType.fca ? 'FCA' : 'FEA'}</span>
                        <span> {record.flags & 0x01 ? '(AFP)' : ''}</span>
                        <span> {record.flags & 0x02 ? '(FSM eligible)' : ''}</span>
                        <span> {record.flags & 0x04 ? '(Extended FEA)' : ''}</span>
                    </div>

                    {record.name && (
                        <>
                            <div>NAS name:</div>
                            <div>
                                <span>{record.name}</span>
                                {shapeNames[record.shape] && <span> ({shapeNames[record.shape]})</span>}
                            </div>
                        </>
                    )}

                    <div>Reason:</div>
                    <div>{record.detail}</div>

                    {record.avgDelay !== null && (
                        <>
                            <div>Average delay:</div>
                            <div>{valueTimesOrNull(record.avgDelay, 1, '-', 'minutes')}</div>
                        </>
                    )}

                    <div>Floor:</div>
                    <div>{valueTimesOrNull(record.floor, 100, '-', 'feet')}</div>

                    <div>Ceiling:</div>
                    <div>{valueTimesOrNull(record.ceiling, 100, '-', 'feet')}</div>

                    <div>Speed:</div>
                    <div>{valueTimesOrNull(record.speed, 1, '-', 'knots')}</div>

                    <div>Direction:</div>
                    <div>{valueTimesOrNull(record.direction, 1, '-', 'degrees')}</div>

                    {(record.fcaStartTime || record.fcaEndTime) && (
                        <>
                            <div>FCA: start/end:</div>
                            <div>
                                {formatter.formatTime(record.fcaStartTime, 'hm') || '?'} -{' '}
                                {formatter.formatTime(record.fcaEndTime, 'hm') || '?'}
                            </div>
                        </>
                    )}

                    {(record.afpStartTime || record.afpEndTime) && (
                        <>
                            <div>AFP start/end:</div>
                            <div>
                                {formatter.formatTime(record.afpStartTime, 'hm') || '?'} -{' '}
                                {formatter.formatTime(record.afpEndTime, 'hm') || '?'}
                            </div>
                        </>
                    )}
                </div>

                <div style={{ whiteSpace: 'pre' }} className="pt-2">
                    {record.filter}
                </div>
            </div>
        </div>
    );
}

function valueTimesOrNull(input: number | null, times: number, nullString: string, post: string): string {
    if (input === null) {
        return nullString;
    }

    return (input * times).toLocaleString('en-US') + ' ' + post;
}

const shapeNames: Record<number, string> = {
    [4]: 'airport',
    [5]: 'sector',
    [6]: 'base sector',
    [7]: 'TRACON',
    [8]: 'base TRACON',
    [9]: 'ARTCC',
    [10]: 'Fix',
    [11]: 'SUA'
};
